import type {
  ApiTabId,
  FilteredListResponse,
  FilteredListSection,
  OngoingResponse,
  QuizCard,
  TestSeriesCard
} from '@api/fetchers/goal/test-tab/types';

import { isCbtProgramme } from '@cont/SeeAll/utils';
import { API_TAB_ID, getSyllabusText } from './constants';
import {
  getSubscriptionBadgeFromApi,
  normalizeSubscriptionFlags
} from './subscription';
import type { TestListingCard, TestSubscriptionBadge } from './types';

const getSubscriptionBadgeFromQuiz = (quiz: QuizCard): TestSubscriptionBadge =>
  getSubscriptionBadgeFromApi(
    normalizeSubscriptionFlags({
      isIconic: quiz.isIconic ?? quiz.programme?.isIconic,
      isSpecial: quiz.isSpecial ?? quiz.programme?.isSpecial
    })
  );

const getSubscriptionBadgeFromSeries = (
  series: TestSeriesCard
): TestSubscriptionBadge =>
  getSubscriptionBadgeFromApi(normalizeSubscriptionFlags(series));

const getQuizSubscriptionMeta = (quiz: QuizCard) => {
  const flags = normalizeSubscriptionFlags({
    isIconic: quiz.isIconic ?? quiz.programme?.isIconic,
    isSpecial: quiz.isSpecial ?? quiz.programme?.isSpecial
  });

  return {
    flags,
    isIconic: flags.isIconic,
    isSpecial: flags.isSpecial,
    isSellable: quiz.isSellable ?? quiz.programme?.isSellable
  };
};

const getSeriesSubscriptionMeta = (series: TestSeriesCard) => {
  const flags = normalizeSubscriptionFlags(series);

  return {
    flags,
    isIconic: flags.isIconic,
    isSpecial: flags.isSpecial,
    isSellable: series.isSellable
  };
};

const toDurationMinutes = (durationSeconds?: number): number => {
  if (!durationSeconds) {
    return 0;
  }

  return Math.max(1, Math.round(durationSeconds / 60));
};

export const mapQuizCardToTestCard = (quiz: QuizCard): TestListingCard => {
  const { isIconic, isSpecial, isSellable } = getQuizSubscriptionMeta(quiz);

  return {
    id: quiz.uid,
    variant: 'quiz',
    categoryLabel: getSyllabusText(quiz.programmeSubtype),
    title: quiz.title || '',
    description: quiz.subtitle || quiz.programme?.name || '',
    subscriptionType: getSubscriptionBadgeFromQuiz(quiz),
    isIconic,
    isSpecial,
    isSellable,
    statusTag: quiz.statusTag,
    questionCount: quiz.numQuestions || 0,
    durationMinutes: toDurationMinutes(quiz.duration),
    isLive: Boolean(quiz.isLive),
    href: quiz.relativeLink || quiz.permalink
  };
};

const formatTestSeriesDate = (dateStr?: string): string => {
  if (!dateStr) {
    return '';
  }

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.getDate()} ${date.toLocaleString('default', {
    month: 'short'
  })}`;
};

const formatTestSeriesDateRange = (
  startsAt?: string,
  endsAt?: string
): string => {
  const startLabel = formatTestSeriesDate(startsAt);
  const endLabel = formatTestSeriesDate(endsAt);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  return startLabel || endLabel;
};

export const mapTestSeriesCardToTestCard = (
  series: TestSeriesCard
): TestListingCard => {
  const { isIconic, isSpecial, isSellable } = getSeriesSubscriptionMeta(series);

  return {
    id: series.uid,
    variant: 'test_series',
    categoryLabel: getSyllabusText(series.programmeSubtype),
    title: series.name || '',
    description: series.description || '',
    subscriptionType: getSubscriptionBadgeFromSeries(series),
    isIconic,
    isSpecial,
    isSellable,
    questionCount: 0,
    durationMinutes: 0,
    testCount: series.quizCount || series.itemCount || 0,
    dateRangeLabel: formatTestSeriesDateRange(series.startsAt, series.endsAt),
    isLive: false,
    isCbtTest: isCbtProgramme(series.programmeType),
    href: series.relativeLink || series.permalink
  };
};

const isFilteredListSection = (
  item: QuizCard | TestSeriesCard | FilteredListSection
): item is FilteredListSection =>
  Array.isArray((item as FilteredListSection).results);

/** Flattens sectioned (`results[]`) or direct card arrays from POST filter */
export const getFilteredListItems = (
  response: FilteredListResponse | undefined
): Array<QuizCard | TestSeriesCard> => {
  const components = response?.components || [];

  if (components.some(isFilteredListSection)) {
    return components.flatMap((item) =>
      isFilteredListSection(item) ? item.results || [] : [item as QuizCard]
    );
  }

  return components as Array<QuizCard | TestSeriesCard>;
};

export const getFilteredListItemCount = (
  response: FilteredListResponse | undefined
): number =>
  getFilteredListItems(response).filter((item) => Boolean(item?.uid)).length;

export const mapListResponseToCards = (
  response: FilteredListResponse | undefined,
  apiTabId: ApiTabId
): TestListingCard[] => {
  const items = getFilteredListItems(response).filter((item) =>
    Boolean(item?.uid)
  );

  if (apiTabId === API_TAB_ID.TEST_SERIES) {
    return items.map((item) =>
      mapTestSeriesCardToTestCard(item as TestSeriesCard)
    );
  }

  return items.map((item) => mapQuizCardToTestCard(item as QuizCard));
};

export const mapOngoingToCards = (
  response: OngoingResponse | undefined
): TestListingCard[] => {
  const liveSection = (response?.components || []).find(
    (section) => section.id === 'LIVE'
  );

  return (liveSection?.results || []).map((item) =>
    mapQuizCardToTestCard({ ...item, isLive: true })
  );
};
